import { useState, useCallback } from 'react';
import type { Batch, LLMRequestInfo, TokenUsage, LearningLanguage } from '../types';

const HISTORY_KEY = 'sentenceHistory';

function loadFromStorage(): Batch[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.warn('Failed to parse sentenceHistory from localStorage', e);
  }
  return [];
}

function saveToStorage(history: Batch[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch (e) {
    console.warn('Failed to save sentenceHistory to localStorage', e);
  }
}

export function useHistory() {
  const [sentenceHistory, setSentenceHistory] = useState<Batch[]>(loadFromStorage);
  const [currentBatchIndex, setCurrentBatchIndex] = useState<number>(() => {
    const h = loadFromStorage();
    return h.length - 1;
  });

  const addBatch = useCallback(
    (
      sentences: string[],
      usedWords: string[],
      mode: string,
      llmRequest?: LLMRequestInfo,
      tokenUsage?: TokenUsage,
      category?: string,
      learningLanguage?: LearningLanguage,
      topic?: string,
      grammaticalCase?: string,
    ): Batch => {
      const batch: Batch = {
        sentences,
        usedWords,
        mode,
        timestamp: Date.now(),
        ...(llmRequest ? { llmRequest } : {}),
        ...(tokenUsage ? { tokenUsage } : {}),
        ...(category ? { category } : {}),
        ...(learningLanguage ? { learningLanguage } : {}),
        ...(topic ? { topic } : {}),
        ...(grammaticalCase ? { grammaticalCase } : {}),
      };
      setSentenceHistory((prev) => {
        const next = [...prev, batch];
        saveToStorage(next);
        setCurrentBatchIndex(next.length - 1);
        return next;
      });
      return batch;
    },
    []
  );

  const deleteBatch = useCallback(() => {
    setSentenceHistory((prev) => {
      if (prev.length === 0) return prev;
      setCurrentBatchIndex((idx) => {
        const next = [...prev];
        next.splice(idx, 1);
        saveToStorage(next);
        // We need to set history inside a timeout to avoid stale closure
        // Instead, compute new index
        if (next.length === 0) {
          setSentenceHistory(next);
          return -1;
        }
        const newIdx = idx >= next.length ? next.length - 1 : idx;
        setSentenceHistory(next);
        return newIdx;
      });
      return prev; // Will be overridden by inner setSentenceHistory
    });
  }, []);

  const navigateBatch = useCallback(
    (direction: number) => {
      setCurrentBatchIndex((idx) => {
        const len = sentenceHistory.length;
        if (len === 0) return idx;
        let next = idx + direction;
        if (next < 0) next = 0;
        if (next >= len) next = len - 1;
        return next;
      });
    },
    [sentenceHistory.length]
  );

  const updateTranslations = useCallback(
    (translations: string[]) => {
      setSentenceHistory((prev) => {
        if (currentBatchIndex < 0 || currentBatchIndex >= prev.length) return prev;
        const next = [...prev];
        next[currentBatchIndex] = { ...next[currentBatchIndex], translations };
        saveToStorage(next);
        return next;
      });
    },
    [currentBatchIndex]
  );

  const setHistory = useCallback((history: Batch[]) => {
    setSentenceHistory(history);
    saveToStorage(history);
    setCurrentBatchIndex(history.length - 1);
  }, []);

  const currentBatch: Batch | null =
    currentBatchIndex >= 0 && currentBatchIndex < sentenceHistory.length
      ? sentenceHistory[currentBatchIndex]
      : null;

  return {
    sentenceHistory,
    currentBatchIndex,
    currentBatch,
    addBatch,
    deleteBatch,
    navigateBatch,
    updateTranslations,
    setHistory,
  };
}
